import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  // Cursor state disabled to reduce network traffic
  // const [cursors, setCursors] = useState({});

  useEffect(() => {
    if (user) {
      // Determine if we should use HTTPS for API URLs
      const determineBackendUrl = () => {
        // Check if we're in production environment and on HTTPS
        if (window.location.protocol === 'https:' && window.location.host === 'crm.sumaroh.com') {
          // Use the HTTPS version of the API
          return 'https://crm-api.sumaroh.com';
        }
        // Otherwise use the configured API URL
        return process.env.REACT_APP_API_URL || 'http://localhost:5001';
      };
      
      const BACKEND_URL = determineBackendUrl();
      console.log('Attempting a socket connection with backend URL:', BACKEND_URL);
      const newSocket = io(BACKEND_URL, {
        auth: {
          token: user.token
        },
        transports: ['websocket', 'polling']
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });
      
      newSocket.on('connect', () => {
        console.log('Connected to socket server');
        const userData = {
          uid: user.uid,
          displayName: user.displayName || user.email || 'Anonymous User',
          photoURL: user.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${user.email || user.uid}`
        };
        console.log('Sending user data:', userData);
        newSocket.emit('user:connect', userData);
      });

      newSocket.on('users:update', (users) => {
        console.log('Online users updated:', users);
        setOnlineUsers(users);
      });

      // Cursor tracking disabled to reduce network traffic
      // newSocket.on('cursors:update', ({ userId, position, user: userData }) => {
      //   // Silently update cursor positions without logging
      //   setCursors(prev => ({
      //     ...prev,
      //     [userId]: { position, user: userData }
      //   }));
      // });

      newSocket.on('lead:updated', (leadData) => {
        console.log('[SOCKET] Received lead:updated event:', { 
          leadId: leadData.leadId,
          callCount: leadData.callCount,
          lastCallTime: leadData.lastCallTime,
          stage: leadData.stage,
          timeReceived: new Date().toLocaleTimeString()
        });
      });
      
      newSocket.on('lead:stage-updated', (data) => {
        console.log('[SOCKET] Received lead:stage-updated event:', {
          leadId: data.leadId,
          newStage: data.stage || data.newStage,
          stageName: data.stageName,
          timeReceived: new Date().toLocaleTimeString()
        });
      });

      newSocket.on('call:added', (data) => {
        console.log('[SOCKET] Received call:added event:', {
          leadId: data.leadId,
          outcome: data.outcome,
          callCount: data.callCount,
          timestamp: data.timestamp,
          timeReceived: new Date().toLocaleTimeString()
        });
      });
      
      newSocket.on('callCount:updated', (data) => {
        console.log('[SOCKET] Received callCount:updated event (backup):', {
          leadId: data.leadId,
          callCount: data.callCount,
          timestamp: data.timestamp,
          timeReceived: new Date().toLocaleTimeString()
        });
      });

      newSocket.on('disconnect', () => {
        console.log('Disconnected from socket server');
      });

      setSocket(newSocket);

      return () => {
        console.log('Cleaning up socket connection');
        newSocket.disconnect();
      };
    }
  }, [user]);

  // Cursor position tracking disabled to reduce network traffic and console noise
  const updateCursorPosition = useCallback((position) => {
    // No-op function that maintains the interface but does nothing
    return;
  }, []);

  // Utility function to emit events with socket ID attached
  const emitWithSourceId = useCallback((eventName, eventData) => {
    if (socket) {
      // Append the socket ID to identify the source of this event
      const dataWithSource = {
        ...eventData,
        _sourceSocketId: socket.id
      };
      console.log(`[SocketContext] Emitting ${eventName} with source ID:`, socket.id);
      socket.emit(eventName, dataWithSource);
    }
  }, [socket]);

  const value = {
    socket,
    onlineUsers,
    // Provide empty object instead of actual cursors to maintain interface
    cursors: {},
    updateCursorPosition,
    emitWithSourceId
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
